import { useEffect, useState } from "react";
import { Zap, Loader2 } from "lucide-react";
import { get } from "@/lib/api";

const isoDate = (d) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, "0");
    const dd = String(d.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

function nextWeekdays(count) {
    const out = [];
    const d = new Date();
    while (out.length < count) {
        if (d.getDay() !== 0 && d.getDay() !== 6) {
            out.push(new Date(d));
        }
        d.setDate(d.getDate() + 1);
    }
    return out;
}

/**
 * Lightweight social-proof / urgency strip — shows how many 30-min consultation
 * slots are open across the next 5 business days. Pulls live availability.
 */
export default function UrgencyRail() {
    const [stats, setStats] = useState({ loading: true, open: 0, total: 0, days: [] });

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const days = nextWeekdays(5);
                const results = await Promise.all(
                    days.map((d) =>
                        get(`/availability?date=${isoDate(d)}`).catch(() => null)
                    )
                );
                if (cancelled) return;
                const ds = days.map((d, i) => ({
                    label: d.toLocaleDateString(undefined, {
                        weekday: "short",
                    }),
                    open: results[i]?.available_slots?.length ?? 0,
                    total: 16,
                }));
                const open = ds.reduce((s, x) => s + x.open, 0);
                const total = ds.reduce((s, x) => s + x.total, 0);
                setStats({ loading: false, open, total, days: ds });
            } catch {
                if (!cancelled) setStats({ loading: false, open: 0, total: 0, days: [] });
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);

    if (stats.loading) {
        return (
            <div
                data-testid="urgency-rail"
                className="mt-6 inline-flex items-center gap-2 text-white/55 text-xs uppercase tracking-[0.2em] font-semibold"
            >
                <Loader2 size={12} className="animate-spin" /> Checking
                availability…
            </div>
        );
    }

    const pct = stats.total ? Math.round((stats.open / stats.total) * 100) : 0;
    const hot = pct <= 30; // <30% slots left → red urgency
    const warm = pct > 30 && pct <= 60;

    return (
        <div
            data-testid="urgency-rail"
            className="mt-6 inline-flex flex-col sm:flex-row sm:items-center gap-3 px-4 py-3 border border-[#1a2a4a] bg-[#020617]/80 backdrop-blur-sm"
        >
            <div className="flex items-center gap-2">
                <Zap
                    size={14}
                    className={
                        hot
                            ? "text-[#fca5a5]"
                            : warm
                            ? "text-[#D4AF37]"
                            : "text-[#00B7FF]"
                    }
                    fill="currentColor"
                />
                <span
                    data-testid="urgency-rail-summary"
                    className="text-xs uppercase tracking-[0.18em] font-bold text-white"
                >
                    {stats.open === 0 ? (
                        <>All consultation slots booked this week</>
                    ) : (
                        <>
                            <span
                                className={
                                    hot
                                        ? "text-[#fca5a5]"
                                        : warm
                                        ? "text-[#D4AF37]"
                                        : "text-[#00B7FF]"
                                }
                            >
                                {stats.open}
                            </span>{" "}
                            slots left this week
                        </>
                    )}
                </span>
            </div>
            <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-[0.15em] text-white/55">
                {stats.days.map((d, i) => (
                    <span
                        key={i}
                        className="flex flex-col items-center"
                        title={`${d.label}: ${d.open} open`}
                    >
                        <span>{d.label}</span>
                        <span
                            className={`mt-0.5 inline-block w-7 h-1 ${
                                d.open === 0
                                    ? "bg-[#1a2a4a]"
                                    : d.open <= 4
                                    ? "bg-[#fca5a5]"
                                    : d.open <= 9
                                    ? "bg-[#D4AF37]"
                                    : "bg-[#00B7FF]"
                            }`}
                        />
                    </span>
                ))}
            </div>
        </div>
    );
}
